import { useTranslation } from 'react-i18next';
import { useSupervisor } from '@/hooks/useSupervisor';
import type { Order } from '@/types/supervisor';
import SetPrinterWindow from './SetPrinterWindow';
import ItemStepPanelLayout from './ItemStepPanelLayout';
import { usePrintableItemTargets, useSelectionGuard } from './hooks/usePrintableItemTargets';

interface PrinterStepPanelProps {
  readonly order: Order;
  readonly onUpdatePrinter?: (itemId: string, printerId: string) => void;
}

const PrinterStepPanel = ({ order, onUpdatePrinter }: PrinterStepPanelProps) => {
  const { t } = useTranslation();
  const { send: sendToSupervisor } = useSupervisor();
  const handleExit = () => {
    sendToSupervisor({ type: 'UI.GOTO_STEP', step: 'uploadedImages' });
  };

  const { printableItems, targetedItems, hasSelectedItems } = usePrintableItemTargets(order);

  useSelectionGuard({
    printableItems,
    hasSelectedItems,
    send: sendToSupervisor,
    aiPrompt: 'Politely explain that the user needs to select at least one uploaded image before choosing a printer.',
    fallbackMessage: t(
      'uploadedImages.aiFallback.printer.noSelection',
      'Select at least one image to choose a printer.',
    ),
  });

  const canApplyToAll = targetedItems.length > 1;

  return (
    <ItemStepPanelLayout<string | null>
      t={t}
      availableItems={printableItems}
      items={targetedItems}
      title={t('canvas.steps.selectItemForPrinter', 'Choose item for printer selection')}
      emptyStateMessage={t('setPrinter.selectItemPrompt', 'Select an item to choose its printer.')}
      uploadCtaLabel={t('orderSummary.goToUpload', 'Upload images')}
      onUploadClick={() => sendToSupervisor({ type: 'UI.GOTO_STEP', step: 'upload' })}
      onExit={handleExit}
      enableUnsavedExitGuard
      pristineSaveLabel={t('setPrinter.confirmCta', 'Confirm printer')}
      dirtySaveLabel={t('setPrinter.saveCta', 'Save printer')}
      discardChangesTitle={t('setPrinter.discardChangesTitle', 'Discard unsaved printer changes?')}
      discardChangesDescription={t('setPrinter.discardChangesDescription', 'Your printer selection is not saved yet. You can keep editing or discard it and leave this screen.')}
      discardChangesConfirmText={t('setPrinter.discardChangesConfirm', 'Discard changes')}
      discardChangesCancelText={t('setPrinter.discardChangesCancel', 'Keep editing')}
      resolveItemValue={(item) => item.printerId ?? null}
      isItemAssigned={(entry) => Boolean(entry?.printerId)}
      onSave={(itemId: string, printerId: string | null) => {
        if (onUpdatePrinter && printerId) {
          onUpdatePrinter(itemId, printerId);
        }
      }}
      renderWindow={({ item, onClose, onBack, onSave, onConfirmSelection, draftValue, setDraftValue, clearDraftValue, setDraftValueForAll, saveLabel }) => (
        <SetPrinterWindow
          key={item.id}
          item={item}
          onClose={onClose}
          onBack={onBack}
          onSave={onSave}
          saveLabel={saveLabel}
          onConfirmSelection={onConfirmSelection}
          draftPrinterId={draftValue ?? undefined}
          onDraftChange={(printerId) => {
            if (printerId === null) {
              setDraftValue(null);
              return;
            }
            setDraftValue(printerId);
          }}
          onDraftReset={() => clearDraftValue()}
          onApplyPrinterToAll={canApplyToAll
            ? async (printerId) => {
                setDraftValue(printerId);
                setDraftValueForAll(printerId);
              }
            : undefined}
        />
      )}
    />
  );
};

export default PrinterStepPanel;
